import { useState } from "react";
import CrudPage from "@/components/CrudPage";
import { useAuth, canWrite } from "@/context/AuthContext";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { rupiah, num } from "@/lib/format";
import api, { formatApiError } from "@/lib/api";
import { Download, Upload, FileSpreadsheet, Loader2 } from "lucide-react";
import { toast } from "sonner";

function PriceImport({ onDone }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const downloadTemplate = async () => {
    try {
      const res = await api.get("/materials-price-template", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = "template_harga_material.xlsx";
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success("Template diunduh");
    } catch (e) {
      toast.error("Gagal mengunduh template");
    }
  };

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setBusy(true);
    setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post("/materials-import-prices", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setResult(res.data);
      toast.success(`${res.data.updated} harga diperbarui`);
      onDone && onDone();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Gagal mengimpor");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Button variant="outline" onClick={() => { setResult(null); setOpen(true); }} data-testid="material-import-open-btn" className="border-green-300 text-green-800 hover:bg-green-50">
        <Upload className="h-4 w-4 mr-1.5" /> Impor Harga
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2"><FileSpreadsheet className="h-5 w-5 text-green-700" /> Impor Harga Material (Excel)</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-1">
            <ol className="text-sm text-slate-600 list-decimal pl-5 space-y-1">
              <li>Unduh template (berisi daftar material saat ini).</li>
              <li>Ubah kolom <b>harga_standar</b>, <b>harga_aktual</b>, <b>sumber_harga</b>, <b>tanggal_update_harga</b>.</li>
              <li>Unggah kembali. Pencocokan berdasarkan kolom <b>code</b>.</li>
            </ol>
            <div className="flex gap-2">
              <Button variant="outline" onClick={downloadTemplate} data-testid="material-template-btn">
                <Download className="h-4 w-4 mr-1.5" /> Unduh Template
              </Button>
              <label className="inline-flex">
                <input type="file" accept=".xlsx,.xls" className="hidden" onChange={onFile} disabled={busy} data-testid="material-import-input" />
                <span className={`inline-flex items-center px-4 py-2 rounded-md text-sm font-medium text-white cursor-pointer ${busy ? "bg-slate-400" : "bg-green-700 hover:bg-green-800"}`}>
                  {busy ? <><Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> Mengimpor...</> : <><Upload className="h-4 w-4 mr-1.5" /> Pilih File & Impor</>}
                </span>
              </label>
            </div>
            {result && (
              <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm space-y-1" data-testid="material-import-result">
                <p className="text-green-700 font-semibold">{result.updated} harga diperbarui</p>
                <p className="text-slate-500">{result.skipped} dilewati dari {result.total_rows} baris.</p>
                {result.errors?.length > 0 && (
                  <ul className="text-xs text-amber-700 list-disc pl-4 mt-1 max-h-32 overflow-y-auto">
                    {result.errors.map((er, i) => <li key={i}>{er}</li>)}
                  </ul>
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Tutup</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default function MasterMaterial() {
  const { user } = useAuth();
  const writeRoles = ["admin_material", "akuntansi"];
  const allowWrite = canWrite(user?.role, writeRoles);
  const [reloadSignal, setReloadSignal] = useState(0);

  return (
    <CrudPage
      title="Master Material"
      subtitle="Material konvensional & ramah lingkungan beserta faktor emisi & sumber harga."
      endpoint="/materials"
      canWrite={allowWrite}
      searchKeys={["code", "name", "kategori", "sumber_harga"]}
      headerActions={allowWrite ? <PriceImport onDone={() => setReloadSignal((v) => v + 1)} /> : null}
      reloadSignal={reloadSignal}
      columns={[
        { key: "code", label: "Kode" },
        { key: "name", label: "Material", render: (r) => (
          <div className="flex items-center gap-2">
            {r.name}
            {r.green_status && <Badge className="bg-green-100 text-green-800 border-green-200 border text-[10px]">GREEN</Badge>}
          </div>
        ) },
        { key: "kategori", label: "Kategori" },
        { key: "harga_aktual", label: "Harga Aktual", align: "right", render: (r) => rupiah(r.harga_aktual) },
        { key: "sumber_harga", label: "Sumber Harga", render: (r) => r.sumber_harga ? <span className="text-slate-700">{r.sumber_harga}</span> : <span className="text-slate-400">-</span> },
        { key: "tanggal_update_harga", label: "Update Harga", render: (r) => r.tanggal_update_harga ? <span className="text-slate-600 tabular-nums">{r.tanggal_update_harga}</span> : <span className="text-slate-400">-</span> },
        { key: "faktor_emisi", label: "Faktor Emisi", align: "right", render: (r) => `${num(r.faktor_emisi, 3)} kg CO₂` },
      ]}
      fields={[
        { key: "code", label: "Kode Material" },
        { key: "name", label: "Nama Material" },
        { key: "jenis", label: "Jenis", type: "select", defaultValue: "konvensional", options: [{ value: "konvensional", label: "Konvensional" }, { value: "ramah_lingkungan", label: "Ramah Lingkungan" }] },
        { key: "kategori", label: "Kategori", type: "select", options: ["Pengikat", "Agregat", "Cairan", "Aditif"].map((v) => ({ value: v, label: v })) },
        { key: "satuan", label: "Satuan", type: "select", options: ["kg", "liter", "m³", "unit"].map((v) => ({ value: v, label: v })) },
        { key: "harga_standar", label: "Harga Standar (Rp)", type: "number" },
        { key: "harga_aktual", label: "Harga Aktual (Rp)", type: "number" },
        { key: "supplier", label: "Supplier" },
        { key: "sumber_harga", label: "Sumber Harga", full: true },
        { key: "tanggal_update_harga", label: "Tanggal Update Harga", type: "date" },
        { key: "faktor_emisi", label: "Faktor Emisi (kg CO₂/unit)", type: "number", step: "0.001" },
        { key: "waste_standar_pct", label: "Waste Standar (%)", type: "number", step: "0.1" },
        { key: "green_status", label: "Material Hijau (Green)", type: "switch" },
        { key: "status", label: "Status", type: "select", defaultValue: "aktif", options: [{ value: "aktif", label: "Aktif" }, { value: "nonaktif", label: "Nonaktif" }] },
      ]}
    />
  );
}
